'use client';

import logoGif from '@/assets/images/common/logo.gif';
import ColorsFamilyFilter from '@/components/filter-section/colors-family-filter';
import ProductSort from '@/components/ProductSort';
import { ACTIVATE_ATTIRE_CATEGORIES } from '@/constants/activate-attire-categories';
import { websiteEnv } from '@/constants/website-env';
import { useAppContext } from '@/context/app';
import useIsMobile from '@/hooks/use-is-mobile';
import {
  GET_CATEGORY_FILTER_OPTIONS,
  GET_CATEGORY_FILTER_OPTIONS_BY_FILTER,
  GET_CATEGORY_PRODUCTS,
  GET_CATEGORY_PRODUCTS_BY_FILTER,
} from '@/typed-graphql/query';
import { gql, useQuery } from '@apollo/client';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { redirect, usePathname, useRouter } from 'next/navigation';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import GalleryAd from '../Gallery/ad';
import GalleryItem from '../Gallery/item';
import CategoryNoIndex from './category-no-index';
import CategoryPagination from './category-pagination';
import FilterAsideLazyLoad from './filter-aside-lazy-load';
import InitCategoryByQueryColor from './init-category-by-query-color';

const CategoryNoResult = dynamic(() => import('./category-no-result'), { ssr: false });
const CategoryDescription = dynamic(() => import('./category-description'), {
  ssr: false,
});
const FixedFilterBarLazyLoad = dynamic(() => import('./fixed-filter-bar-lazy-load'), {
  ssr: false,
});
const FilterModal = dynamic(() => import('@/components/filter-section/filter-modal'), {
  ssr: false,
});

const toSortedFilterOptions = prevOptions => {
  return prevOptions.slice(0).sort((a, b) => {
    const positionA = a.position;
    const positionB = b.position;

    if (positionA === positionB) return 0;

    if (positionA === null && positionB !== null) return 1;

    if (positionA !== null && positionB === null) return -1;

    return positionA - positionB;
  });
};

const CategoryContent = props => {
  const {
    name,
    description,
    categoryUrlKey,
    category_image,
    uid,
    currentPage,
    filterState,
    sortState,
    aggregations,
    availableSortFields,
    layerFilterLabels,
    titleH1,
    colorsFamilyOptions,
    hideColorsFamily,
    breadCrumb,
    categoryFilter,
    filterColor,
    pageSize,
    defaultCategoryState,
    isOutOfPagination,
    breadCrumbInfo,
    useServerState,
    categoryAllFilterOptions,
    categoryIntId,
  } = props;

  const isAttireCategory =
    ACTIVATE_ATTIRE_CATEGORIES.includes(breadCrumbInfo?.uid) ||
    (Array.isArray(breadCrumbInfo?.breadcrumbs) &&
      breadCrumbInfo.breadcrumbs.some(x => ACTIVATE_ATTIRE_CATEGORIES.includes(x?.category_uid)));

  const isMobile = useIsMobile();
  const pathname = usePathname();
  const router = useRouter();
  const [
    { headerOnTop, headerToolbarHeight, headerHeight },
    {
      actions: { setActiveColumn },
    },
  ] = useAppContext();

  const headerVisibleHeight = headerHeight - (headerOnTop ? 0 : headerToolbarHeight);

  const { ref: staticFilterBarRef, inView: staticFilterBarInView } = useInView({
    rootMargin: `-${headerHeight - headerVisibleHeight}px 0px 0px 0px`,
    threshold: 0,
    skip: !isMobile,
    initialInView: true,
  });

  const [clientFilterState, setClientFilterState] = useState(filterState);
  const [internalFilterState, setInternalFilterState] = useState(filterState);
  const fixedFilterBarInView = !staticFilterBarInView;
  const [clientCategoryFilter, setClientCategoryFilter] = useState(categoryFilter);
  const [categoryFilterLabels, setCategoryFilterLabels] = useState(layerFilterLabels);
  const [clientFilterColor, setClientFilterColor] = useState(filterColor);
  const prevPropsRef = useRef({ filterState });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isInitBySearchParams, setIsInitBySearchParams] = useState(true);

  if (prevPropsRef.current.filterState !== filterState) {
    prevPropsRef.current.filterState = filterState;
    setInternalFilterState(filterState);
    setClientFilterState(filterState);
  }

  useEffect(() => {
    setActiveColumn(isAttireCategory ? 'attire' : 'home');
  }, [isAttireCategory, setActiveColumn]);

  const { data: filteredCategoryOptionsRes, loading: filteredCategoryOptionsResLoading } = useQuery(
    gql(internalFilterState.length === 0 ? GET_CATEGORY_FILTER_OPTIONS : GET_CATEGORY_FILTER_OPTIONS_BY_FILTER),
    {
      variables: {
        filter: clientCategoryFilter,
      },
      skip: useServerState || isInitBySearchParams,
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: categoryProductsRes, loading: categoryProductsResLoading } = useQuery(
    gql(internalFilterState.length === 0 ? GET_CATEGORY_PRODUCTS : GET_CATEGORY_PRODUCTS_BY_FILTER),
    {
      variables: {
        currentPage: currentPage,
        id: uid,
        filters: clientCategoryFilter,
        pageSize,
        sort: sortState
          ? {
              [sortState.sortAttribute]: sortState.sortDirection,
            }
          : {},
        filter_color: clientFilterColor,
      },
      skip: useServerState || isInitBySearchParams,
      fetchPolicy: 'cache-and-network',
      onCompleted(data) {
        const responseTotalPage = data?.products?.page_info?.total_pages || 0;
        const items = data?.products?.items || [];
        const nextPathname = pathname
          .split('/')
          .reduce((acc, cur, i, arr) => {
            if (cur === 'p' || (i > 0 && arr[i - 1] === 'p' && /^\d+$/.test(cur))) {
              return acc;
            }

            acc.push(cur);

            return acc;
          }, [])
          .join('/');

        if (items.length === 0 && responseTotalPage < currentPage && pathname !== nextPathname) {
          const search = window.location.search;
          router.replace(nextPathname + (search ? `?${search}` : ''));
        }
      },
    },
  );
  const filteredCategoryOptionsLoading = filteredCategoryOptionsResLoading || isInitBySearchParams;
  const categoryProductsLoading = categoryProductsResLoading || isInitBySearchParams;

  const { categoryAdvs, categoryProducts, totalPages, totalCount } = (() => {
    if (useServerState) {
      return defaultCategoryState;
    }

    const categoryAdvs = categoryProductsRes?.categories?.items?.[0]?.adv || [];
    const categoryProductsInfo = categoryProductsRes?.products;
    const categoryProducts = categoryProductsInfo?.items || [];
    const totalPages = categoryProductsInfo?.page_info?.total_pages || 0;
    const totalCount = categoryProductsInfo?.total_count || 0;

    return {
      categoryAdvs,
      categoryProducts,
      totalPages,
      totalCount,
    };
  })();

  const filterOptions = useMemo(() => {
    if (useServerState) {
      return toSortedFilterOptions(defaultCategoryState.filterOptions);
    }

    const filteredCategoryFilterOptions = filteredCategoryOptionsRes?.products?.aggregations || [];

    return toSortedFilterOptions(filteredCategoryFilterOptions);
  }, [filteredCategoryOptionsRes, useServerState, defaultCategoryState]);

  const filterBarColorOptions = filterOptions.find(x => x?.attribute_code === 'custom_option_color_ids')?.options || [];

  const filterLabelGroup =
    categoryFilterLabels.length > 0 ? categoryFilterLabels.map(x => x.split(',').join(', ')).join(', ') : '';

  useEffect(() => {
    if (!globalThis.scrollTo) {
      return;
    }

    globalThis.scrollTo({
      behavior: 'smooth',
      left: 0,
      top: 0,
    });
  }, [currentPage]);

  const freeShipping = uid === 'MTYy' || uid === 'OQ==';

  useEffect(() => {
    if (!isOutOfPagination) return;

    const search = window.location.search;
    const nextPathname = pathname
      .split('/')
      .reduce((acc, cur, i, arr) => {
        if (cur === 'p' || (i > 0 && arr[i - 1] === 'p' && /^\d+$/.test(cur))) {
          return acc;
        }

        acc.push(cur);

        return acc;
      }, [])
      .join('/');

    if (nextPathname === pathname) return;

    redirect(nextPathname + (search ? `?${nextPathname}` : ''));
  }, [isOutOfPagination, pathname, router]);

  const optionIdMapToRewrite = useMemo(() => {
    return aggregations.reduce((acc, cur) => {
      const { option_id, rewrite, code } = cur;

      if (!rewrite || !option_id || !code) {
        return acc;
      }

      if (acc[code]) {
        acc[code][option_id] = rewrite;
      } else {
        acc[code] = {
          [option_id]: rewrite,
        };
      }

      return acc;
    }, {});
  }, [aggregations]);

  const showColorsFamilyFilter =
    !hideColorsFamily &&
    Array.isArray(colorsFamilyOptions) &&
    colorsFamilyOptions.length &&
    Array.isArray(aggregations) &&
    aggregations.length;

  const content = (() => {
    if (categoryProductsLoading) {
      return (
        <div className='grid grid-cols-2 gap-x-2 gap-y-[30px] lg_grid-cols-4 lg_gap-y-[54px]'>
          {Array.from(
            {
              length: 36,
            },
            (_, i) => (
              <div key={i}>
                <Image
                  src={logoGif}
                  width={280}
                  height={410}
                  alt='placeholder'
                  className='h-auto w-full'
                />
              </div>
            ),
          )}
        </div>
      );
    }

    if (categoryProducts.length === 0) {
      return <CategoryNoResult url_key={categoryUrlKey} />;
    }

    const { advCount, advMap } = categoryAdvs.reduce(
      (acc, cur) => {
        acc.advCount++;
        acc.advMap[cur.position - 1] = cur;

        return acc;
      },
      {
        advCount: 0,
        advMap: {},
      },
    );
    let productIndex = 0;

    return (
      <>
        <section className='grid grid-cols-2 gap-x-2 gap-y-[30px] lg_grid-cols-4 lg_gap-y-[54px]'>
          {Array.from({ length: categoryProducts.length + advCount }).reduce((acc, cur, i) => {
            const adv = advMap[i];

            if (adv) {
              acc.push(
                <GalleryAd
                  key={adv.id}
                  item={adv}
                />,
              );
              return acc;
            }

            const product = productIndex < categoryProducts.length ? categoryProducts[productIndex] : null;

            if (product) {
              acc.push(
                <GalleryItem
                  key={product.id}
                  item={product}
                  freeShipping={freeShipping}
                  inCategoryPage
                  categoryName={name}
                  categoryId={uid}
                  showSpecial12Off
                />,
              );
            }
            productIndex++;

            return acc;
          }, [])}
        </section>
        {totalPages > 1 ? (
          <Suspense>
            <div className='mt-[14px] lg_mt-[38px]'>
              <CategoryPagination
                currentPage={currentPage}
                totalPages={totalPages}
              />
            </div>
          </Suspense>
        ) : null}
      </>
    );
  })();

  return (
    <>
      <Suspense>
        <CategoryNoIndex filterState={useServerState ? filterState : clientFilterState} />
        <InitCategoryByQueryColor
          useServerState={useServerState}
          setClientCategoryFilter={setClientCategoryFilter}
          categoryUrlKey={categoryUrlKey}
          aggregations={aggregations}
          filterOptions={categoryAllFilterOptions}
          categoryIntId={categoryIntId}
          setCategoryFilterLabels={setCategoryFilterLabels}
          setClientFilterState={setClientFilterState}
          setInternalFilterState={setInternalFilterState}
          setClientFilterColor={setClientFilterColor}
          setIsInitBySearchParams={setIsInitBySearchParams}
        />
      </Suspense>
      {isMobile && filterOptions.length ? (
        <FilterModal
          filterOptions={filterOptions}
          filterState={internalFilterState}
          curCategoryUrlKey={categoryUrlKey}
          isSearchPage={false}
          hideColorsFamily={hideColorsFamily}
          open={showFilterModal}
          onSetOpen={nextOpen => {
            setShowFilterModal(nextOpen);
          }}
          colorsFamilyOptions={colorsFamilyOptions}
          optionIdMapToRewrite={optionIdMapToRewrite}
          aggregations={aggregations}
        />
      ) : null}

      <div className='hidden py-[60px] lg_block'>
        <h1 className='px-2.5 text-center font-Shango text-[32px] leading-[38px]'>
          {filterLabelGroup ? `${filterLabelGroup} ${name}` : name}
        </h1>
        <p className='mt-5 text-center text-[17px] leading-6'>{titleH1}</p>
      </div>

      {breadCrumb}

      <article className='mx-auto  mt-[31px] max-w-[1600px] pb-[45px]  lg_mt-10'>
        {category_image ? (
          <div className='mb-[56px] hidden h-[335px] w-full px-2.5 lg_block lg_p-0'>
            <Image
              width={1440}
              height={335}
              src={category_image}
              loading='eager'
              alt={name}
            />
          </div>
        ) : null}
        <div className='flex items-center justify-between gap-x-4 px-2.5 lg_hidden lg_px-0'>
          <h1 className='font-Shango  text-xl leading-6 '>{filterLabelGroup ? `${filterLabelGroup} ${name}` : name}</h1>
          <div className='flex-none text-sm leading-[18px] lg_text-8B857D'>
            {totalCount} Result{totalCount > 1 ? 's' : ''}
          </div>
        </div>
        {showColorsFamilyFilter ? (
          <div className='mx-auto mt-[30px] max-w-site lg_mt-0'>
            <div className='hidden text-center text-base font-medium leading-[22px] lg_block'>
              {websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR'}
            </div>
            <ColorsFamilyFilter
              colorsFamilyOptions={colorsFamilyOptions}
              aggregations={aggregations}
              optionIdMapToRewrite={optionIdMapToRewrite}
              categoryUrlKey={categoryUrlKey}
              className='lg_mt-[25px] '
              filterState={internalFilterState}
            />
          </div>
        ) : null}
        <div className='mt-[37px] w-full px-2.5 lg_flex lg_gap-x-[29px] lg_px-0'>
          {!useServerState && filteredCategoryOptionsLoading && !filteredCategoryOptionsRes ? (
            <div className='hidden w-[269px] flex-none lg_block'></div>
          ) : (
            <FilterAsideLazyLoad
              filterOptions={filterOptions}
              filterState={internalFilterState}
              onSetFilterState={nextFilterState => {
                setInternalFilterState(nextFilterState);
              }}
              curCategoryUrlKey={categoryUrlKey}
              isSearchPage={false}
              hideColorsFamily={hideColorsFamily}
              optionIdMapToRewrite={optionIdMapToRewrite}
              headerVisibleHeight={headerVisibleHeight}
            />
          )}
          <div className='lg_flex-1'>
            <div
              ref={staticFilterBarRef}
              className='flex items-center justify-between'
            >
              <div className='hidden flex-none text-sm leading-[18px] lg_block lg_text-8B857D'>
                {totalCount} Result{totalCount > 1 ? 's' : ''}
              </div>
              <div
                className={clsx(' grid flex-1 items-center lg_flex lg_flex-none lg_gap-x-2', ' grid-cols-2 gap-x-2')}
              >
                {filterOptions.length > 0 ? (
                  <button
                    className={clsx(
                      'flex flex-1 items-center justify-center bg-f8f3ef lg_hidden ',
                      'gap-x-2.5 py-[13px]',
                    )}
                    type='button'
                    aria-live='polite'
                    aria-busy='false'
                    onClick={() => {
                      setShowFilterModal(true);
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='17'
                      height='12'
                      fill='none'
                      viewBox='0 0 17 12'
                    >
                      <g clipPath='url(#clip0_10578_75403)'>
                        <path
                          fill='#80583F'
                          d='M7.558 1.716a2.14 2.14 0 0 0-.75-1.232 2.174 2.174 0 0 0-2.737 0 2.14 2.14 0 0 0-.75 1.232H.25v.858h3.07c.1.484.365.92.75 1.232a2.174 2.174 0 0 0 2.738 0 2.14 2.14 0 0 0 .75-1.232h8.692v-.858zM5.44 3.431a1.3 1.3 0 0 1-.746-.239 1.3 1.3 0 0 1-.551-1.047q.005-.22.078-.429c.087-.252.25-.47.47-.626a1.297 1.297 0 0 1 2.046 1.055q-.005.22-.077.429c-.09.249-.256.465-.474.618a1.3 1.3 0 0 1-.746.239'
                        />
                        <path
                          fill='#000'
                          fillOpacity='0.2'
                          d='M7.558 1.716a2.14 2.14 0 0 0-.75-1.232 2.174 2.174 0 0 0-2.737 0 2.14 2.14 0 0 0-.75 1.232H.25v.858h3.07c.1.484.365.92.75 1.232a2.174 2.174 0 0 0 2.738 0 2.14 2.14 0 0 0 .75-1.232h8.692v-.858zM5.44 3.431a1.3 1.3 0 0 1-.746-.239 1.3 1.3 0 0 1-.551-1.047q.005-.22.078-.429c.087-.252.25-.47.47-.626a1.297 1.297 0 0 1 2.046 1.055q-.005.22-.077.429c-.09.249-.256.465-.474.618a1.3 1.3 0 0 1-.746.239'
                        />
                        <path
                          fill='#80583F'
                          d='M11.06 7.711c-.498 0-.98.17-1.367.483a2.14 2.14 0 0 0-.751 1.232H.25v.857h8.692c.073.51.33.978.723 1.316a2.174 2.174 0 0 0 2.834 0c.394-.338.65-.805.724-1.316h3.027v-.857h-3.027a2.14 2.14 0 0 0-.768-1.245 2.17 2.17 0 0 0-1.394-.47m0 3.429a1.3 1.3 0 0 1-.745-.239 1.3 1.3 0 0 1-.474-.618 1.22 1.22 0 0 1 0-.857c.087-.252.252-.471.47-.626a1.297 1.297 0 0 1 1.97.626c.103.276.103.58 0 .857a1.313 1.313 0 0 1-1.22.857'
                        />
                        <path
                          fill='#000'
                          fillOpacity='0.2'
                          d='M11.06 7.711c-.498 0-.98.17-1.367.483a2.14 2.14 0 0 0-.751 1.232H.25v.857h8.692c.073.51.33.978.723 1.316a2.174 2.174 0 0 0 2.834 0c.394-.338.65-.805.724-1.316h3.027v-.857h-3.027a2.14 2.14 0 0 0-.768-1.245 2.17 2.17 0 0 0-1.394-.47m0 3.429a1.3 1.3 0 0 1-.745-.239 1.3 1.3 0 0 1-.474-.618 1.22 1.22 0 0 1 0-.857c.087-.252.252-.471.47-.626a1.297 1.297 0 0 1 1.97.626c.103.276.103.58 0 .857a1.313 1.313 0 0 1-1.22.857'
                        />
                      </g>
                      <defs>
                        <clipPath id='clip0_10578_75403'>
                          <path
                            fill='#fff'
                            d='M.25 0h16v12h-16z'
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className={clsx('text-80583F lg_text-base lg_leading-6', 'text-[17px]  leading-6')}>
                      FILTER ({useServerState ? filterState.length : clientFilterState.length})
                    </span>
                  </button>
                ) : (
                  <div className='flex flex-1 items-center justify-center gap-x-2.5 py-[13px]  lg_hidden'></div>
                )}
                {availableSortFields.length > 0 && (
                  <Suspense fallback={<div className='h-[50px] lg_h-11'></div>}>
                    <ProductSort
                      sortState={sortState}
                      availableSortMethods={availableSortFields}
                      inFilterBar={false}
                    />
                  </Suspense>
                )}
              </div>
            </div>
            <div className='mt-1.5 lg_mt-[15px] '>{content}</div>
            {String(currentPage) === '1' && (
              <CategoryDescription
                description={description}
                filterLabelGroup={filterLabelGroup}
              />
            )}
          </div>
        </div>
      </article>

      {fixedFilterBarInView ? (
        <FixedFilterBarLazyLoad
          staticFilterBarInView={staticFilterBarInView}
          setShowFilterModal={setShowFilterModal}
          filterStateCount={useServerState ? filterState.length : clientFilterState.length}
          sortState={sortState}
          availableSortFields={availableSortFields}
          colorsFamilyOptions={colorsFamilyOptions}
          filterBarColorOptions={filterBarColorOptions}
          aggregations={aggregations}
          optionIdMapToRewrite={optionIdMapToRewrite}
          categoryUrlKey={categoryUrlKey}
          internalFilterState={internalFilterState}
          hideColorsFamily={hideColorsFamily}
        />
      ) : null}
    </>
  );
};

export default CategoryContent;
